import { Injectable } from '@angular/core';
import { ClientVersion } from '../models/client-version.model';
import { User } from '../models/user.model';
import { ServiceLocation } from '../models/service-location.model';

interface Value<T> {
  value: T | null;
  ts: number;
  ttl: number;
}

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  private readonly CLIENT_VERSION_KEY = 'client_version';
  private readonly DEFAULT_TTL = 24 * 60 * 60 * 1000;
  private readonly SERVICE_LOCATIONS_KEY = 'service_locations';
  private readonly USER_KEY = 'user';

  // user methods
  setUser(user: User | null, ttl: number = this.DEFAULT_TTL): void {
    this.set(this.USER_KEY, user, ttl);
  }
  getUser(): User | null {
    return this.get<User>(this.USER_KEY);
  }

  clearUser(): void {
    this.remove(this.USER_KEY);
  }

  // client version methods
  setClientVersion(clientVersion: ClientVersion | null, ttl: number = this.DEFAULT_TTL): void {
    this.set(this.CLIENT_VERSION_KEY, clientVersion, ttl);
  }

  getClientVersion(): ClientVersion | null {
    return this.get<ClientVersion>(this.CLIENT_VERSION_KEY);
  }

  clearClientVersion(): void {
    this.remove(this.CLIENT_VERSION_KEY);
  }

  // service locations methods
  setServiceLocations(
    serviceLocations: ServiceLocation[] | null,
    ttl: number = this.DEFAULT_TTL,
  ): void {
    this.set(this.SERVICE_LOCATIONS_KEY, serviceLocations, ttl);
  }

  getServiceLocations(): ServiceLocation[] | null {
    return this.get<ServiceLocation[]>(this.SERVICE_LOCATIONS_KEY);
  }

  clearServiceLocations(): void {
    this.remove(this.SERVICE_LOCATIONS_KEY);
  }

  // private methods
  private set<T>(key: string, value: T | null, ttl: number): void {
    const payload: Value<T> = { value, ts: Date.now(), ttl };
    localStorage.setItem(key, JSON.stringify(payload));
  }

  private get<T>(key: string): T | null {
    const item = localStorage.getItem(key);
    if (!item) {
      return null;
    }
    try {
      const data = JSON.parse(item) as Value<T>;
      if (Date.now() - data.ts > data.ttl) {
        this.remove(key);
        return null;
      }
      return data.value;
    } catch {
      this.remove(key);
      return null;
    }
  }

  private remove(key: string): void {
    localStorage.removeItem(key);
  }
}
